import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FaChevronDown, FaFilter, FaTimes } from 'react-icons/fa';
import { NS } from '../../U3301';
import type { U3301InitLUCategory } from '../../U3301Service';
import { u3301IsHexColor, u3301ToggleCategoryFilter } from '../../utils/u3301AgendaDateRange';

interface U3301CategoryFilterDropdownProps {
  categories: U3301InitLUCategory[];
  categoryIds: string[];
  onCategoryIdsChange: (ids: string[]) => void;
  className?: string;
}

function isCategoryChecked(categoryIds: string[], id: string): boolean {
  return categoryIds.length === 0 || categoryIds.includes(id);
}

const U3301CategoryFilterDropdown: React.FC<U3301CategoryFilterDropdownProps> = ({
  categories,
  categoryIds,
  onCategoryIdsChange,
  className = '',
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId().replace(/:/g, '');
  const generatedMenuId = `u3301-category-filter-${menuId}`;

  const allIds = categories.map((c) => c.id);
  const hasActiveFilter = categoryIds.length > 0;
  const activeCount = hasActiveFilter ? categoryIds.length : categories.length;

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const toggle = (id: string) => {
    onCategoryIdsChange(u3301ToggleCategoryFilter(categoryIds, allIds, id));
  };

  return (
    <div ref={containerRef} className={`relative inline-block ${className}`}>
      <button
        type="button"
        className="box-border inline-flex h-[46px] min-h-[46px] items-center justify-center rounded-lg border border-gray-300 bg-white px-3 py-0 text-sm font-medium text-gray-800 shadow-xs transition-all duration-200 hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-blue-500/40 focus:ring-offset-2"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-controls={isOpen ? generatedMenuId : undefined}
        title={t('btnFilter', { ns: NS })}
      >
        <FaFilter className="mr-2 h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>{t('btnFilter', { ns: NS })}</span>
        {hasActiveFilter && (
          <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-600 px-1.5 text-xs font-semibold text-white">
            {activeCount}
          </span>
        )}
        <FaChevronDown className="ml-2 h-3 w-3 shrink-0 text-gray-500" aria-hidden />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            id={generatedMenuId}
            role="menu"
            aria-label={t('btnFilter', { ns: NS })}
            className="fixed z-1000 min-w-[260px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
            style={{
              top: (containerRef.current?.getBoundingClientRect().bottom ?? 0) + 4,
              left: Math.max(
                8,
                (containerRef.current?.getBoundingClientRect().right ?? 268) - 268,
              ),
              width: 268,
            }}
          >
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => onCategoryIdsChange([])}
              disabled={!hasActiveFilter}
            >
              <FaTimes className="h-3.5 w-3.5 shrink-0 text-gray-500" aria-hidden />
              <span>{t('btnClearFilters', { ns: NS })}</span>
            </button>

            <div className="my-1 border-t border-gray-200" role="separator" />

            <div className="max-h-72 overflow-y-auto py-1">
              {categories.length === 0 ? (
                <p className="px-3 py-2 text-sm text-gray-500">{t('lblLegend', { ns: NS })}</p>
              ) : (
                categories.map((cat) => {
                  const checked = isCategoryChecked(categoryIds, cat.id);
                  const color = u3301IsHexColor(cat.effectiveColorCode)
                    ? cat.effectiveColorCode
                    : '#64748b';
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={checked}
                      className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50"
                      onClick={() => toggle(cat.id)}
                    >
                      <span
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                          checked ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white'
                        }`}
                        aria-hidden
                      >
                        {checked && (
                          <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none">
                            <path
                              d="M2.5 6.5L5 9l4.5-5.5"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </span>
                      <span
                        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: color }}
                      />
                      <span className="min-w-0 truncate" title={cat.code}>
                        {cat.label}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default U3301CategoryFilterDropdown;
